import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, CheckCircle2, RotateCw, Layers, Clock, Target, Info } from 'lucide-react';
import trainingApi from './api';
import { useTrainingLang } from './i18n/TrainingLangContext';
import { nativeLabelFor } from './i18n/strings';

const TrainingDashboard = () => {
  const { language, t } = useTrainingLang();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Re-fetch on language change: titles, descriptions and slide counts are all
  // per-language, so the whole list is language-dependent.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const data = await trainingApi.listTrainings(language);
        if (!mounted) return;
        setTrainings(data);
        setError('');
      } catch (e) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [language]);

  const statusOf = (tr) => {
    if (tr.best_score !== null && tr.best_score !== undefined) return 'passed';
    if (tr.attempt_count > 0) return 'in_progress';
    return 'not_started';
  };

  return (
    <div className="p-8 text-slate-100">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">{t('dashboardTitle')}</h1>
        <p className="text-slate-400 text-sm">{t('dashboardSubtitle')}</p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-400">{t('loading')}</div>
      ) : trainings.length === 0 ? (
        <div className="text-slate-400">{t('dashboardEmpty')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {trainings.map((tr) => {
            const status = statusOf(tr);
            const languages = Array.isArray(tr.languages) ? tr.languages : [];
            return (
              <div
                key={tr.id}
                className="rounded-lg bg-slate-800 border border-slate-700 p-5 flex flex-col"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="text-lg font-semibold">{tr.title}</h3>
                  <StatusBadge status={status} score={tr.best_score} t={t} />
                </div>
                {tr.description && (
                  <p className="text-sm text-slate-400 mb-3 line-clamp-3">{tr.description}</p>
                )}

                {/* This training exists in these languages. Only shown when there is a
                    real choice — a single-language training needs no chips. */}
                {languages.length > 1 && (
                  <div className="flex items-center flex-wrap gap-1.5 mb-3 text-xs">
                    <span className="text-slate-500">{t('availableIn')}</span>
                    {languages.map((code) => (
                      <span
                        key={code}
                        className={`px-1.5 py-0.5 rounded border ${
                          code === tr.language
                            ? 'border-amber-600 bg-amber-900/30 text-amber-200'
                            : 'border-slate-600 text-slate-400'
                        }`}
                      >
                        {nativeLabelFor(code)}
                      </span>
                    ))}
                  </div>
                )}

                {/* The user asked for a language this training does not have yet. Say so
                    plainly instead of silently handing them an English deck. */}
                {tr.language_fallback && (
                  <div className="flex items-start gap-1.5 mb-3 text-xs text-amber-300/90">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{t('fallbackNotice', { language: nativeLabelFor(language) })}</span>
                  </div>
                )}

                <div className="flex items-center gap-4 text-xs text-slate-400 mb-4 mt-auto">
                  <span className="inline-flex items-center gap-1">
                    <Layers className="w-3.5 h-3.5" />
                    {t('slideCount', { count: tr.slide_count })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Target className="w-3.5 h-3.5" />
                    {t('passThreshold', { pct: tr.pass_threshold_pct })}
                  </span>
                  {tr.time_limit_seconds ? (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {t('timeLimit', { minutes: Math.round(tr.time_limit_seconds / 60) })}
                    </span>
                  ) : null}
                </div>

                <Link
                  to={`/tiles/ksp_training/take/${tr.id}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium"
                >
                  {status === 'passed' ? (
                    <><CheckCircle2 className="w-4 h-4" /> {t('actionReview')}</>
                  ) : status === 'in_progress' ? (
                    <><RotateCw className="w-4 h-4" /> {t('actionRetry')}</>
                  ) : (
                    <><Play className="w-4 h-4" /> {t('actionStart')}</>
                  )}
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status, score, t }) => {
  if (status === 'passed') {
    return (
      <span className="px-2 py-1 rounded text-xs font-medium bg-emerald-900/40 text-emerald-300 border border-emerald-800 whitespace-nowrap">
        {t('statusPassed')} {score !== null && score !== undefined ? `· ${Number(score).toFixed(0)}%` : ''}
      </span>
    );
  }
  if (status === 'in_progress') {
    return (
      <span className="px-2 py-1 rounded text-xs font-medium bg-amber-900/40 text-amber-300 border border-amber-800 whitespace-nowrap">
        {t('statusInProgress')}
      </span>
    );
  }
  return (
    <span className="px-2 py-1 rounded text-xs font-medium bg-slate-700/50 text-slate-300 border border-slate-600 whitespace-nowrap">
      {t('statusNotStarted')}
    </span>
  );
};

export default TrainingDashboard;
